import classNames from 'classnames'
import styles from './server/elements/Table.module.css'
import { type RefObject, memo, useCallback, useEffect, useRef } from 'react'
import { useResizeContext } from './utils/TableStore'
function calcTotalOffset(ele: HTMLElement, initialOffset = 0): number {
  let parent = ele
  let offset = initialOffset
  while (parent !== null) {
    offset += parent.offsetLeft
    parent = parent.offsetParent as HTMLElement
  }
  return offset
}
function isTouchEvent(evt: MouseEvent | TouchEvent): evt is TouchEvent {
  return 'targetTouches' in evt
}
function useResize<T extends HTMLElement>(ref: RefObject<T | null>, index: number) {
  const { onResize, setResizing, touchEnabled } = useResizeContext()
  const onResizeRef = useRef(onResize)
  onResizeRef.current = onResize
  const animRef = useRef<number>(null)
  const onMouseMove = useCallback(
    (evt: MouseEvent | TouchEvent) => {
      const ele = ref.current
      if (animRef.current) {
        globalThis.cancelAnimationFrame(animRef.current)
      }
      animRef.current = window.requestAnimationFrame(() => {
        if (!ele) return
        const offset = calcTotalOffset(ele)
        let clientX = offset
        if (isTouchEvent(evt)) {
          clientX = evt.targetTouches[0]?.clientX ?? offset
        } else {
          clientX = evt.clientX
        }
        const delta = Math.round(clientX) - offset
        onResizeRef.current(delta, index)
        animRef.current = null
      })
    },
    [ref, animRef, onResizeRef, index]
  )

  const removeMoveListeners = useCallback(() => {
    const ele = ref.current
    if (!ele) return
    setResizing(false)
    if (touchEnabled) {
      window.removeEventListener('touchmove', onMouseMove)
      window.removeEventListener('touchend', removeMoveListeners)
      return
    }
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', removeMoveListeners)
  }, [onMouseMove, ref, touchEnabled, setResizing])

  const addMoveListeners = useCallback(() => {
    const ele = ref.current
    if (!ele) return
    removeMoveListeners()
    setResizing(true)
    if (touchEnabled) {
      window.addEventListener('touchmove', onMouseMove)
      window.addEventListener('touchend', removeMoveListeners)
      return
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', removeMoveListeners)
  }, [onMouseMove, ref, removeMoveListeners, touchEnabled, setResizing])

  const addListeners = useCallback(
    (ele: HTMLElement | null) => {
      if (!ele) return
      if (touchEnabled) {
        ele.addEventListener('touchstart', addMoveListeners)
        return
      }
      ele.addEventListener('mousedown', addMoveListeners)
    },

    [addMoveListeners, touchEnabled]
  )

  const removeListeners = useCallback(
    (ele: HTMLElement | null) => {
      if (!ele) return
      if (typeof animRef.current === 'number') {
        globalThis.cancelAnimationFrame(animRef.current)
      }
      removeMoveListeners()
      if (touchEnabled) {
        ele.removeEventListener('touchstart', addMoveListeners)
        return
      }
      ele.removeEventListener('mousedown', addMoveListeners)
    },
    [removeMoveListeners, addMoveListeners, animRef, touchEnabled]
  )

  useEffect(() => {
    const ele = ref.current
    addListeners(ele)
    return () => removeListeners(ele)
  }, [ref, addListeners, removeListeners])
}

function Resize({ className, index }: { className?: string; index: number }) {
  const ref = useRef<HTMLDivElement | null>(null)
  useResize(ref, index)
  return <div className={classNames(styles.resizeHandle, className)} ref={ref} />
}

export default memo(Resize)
